import React, { useState, useEffect } from 'react';
import { Lock, Eye, EyeOff, ArrowRight, ArrowLeft, CheckCircle, AlertCircle, Loader, XCircle } from 'lucide-react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { authService } from '@/services';
import { trackAuth, trackForm} from '@/services/analytics';
import { useScrollTracking } from '@/hooks/useAnalytics';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [tokenEmail, setTokenEmail] = useState('');
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [formStarted, setFormStarted] = useState(false);

  // Track page view and scroll depth
  useScrollTracking('ResetPassword');

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setIsValidating(false);
        setIsTokenValid(false);
        return;
      }

      try {
        const response = await authService.validateResetToken(token);
        if (response.success && response.data?.valid) {
          setIsTokenValid(true);
          setTokenEmail(response.data.email || '');
        } else {
          setIsTokenValid(false);
        }
      } catch (err) {
        setIsTokenValid(false);
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token]);

  // Track field focus
  const handleFieldFocus = (fieldName: string) => {
    if (!formStarted) {
      trackForm.formStart('reset-password-form', 'Reset Password Form');
      setFormStarted(true);
    }
    trackForm.fieldFocus('reset-password-form', fieldName);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate passwords
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      trackForm.formError('reset-password-form', 'Reset Password Form', 'Password too short', 'password');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      trackForm.formError('reset-password-form', 'Reset Password Form', 'Passwords do not match', 'confirmPassword');
      return;
    }

    setIsLoading(true);

    // Track form submission
    trackForm.formSubmit('reset-password-form', 'Reset Password Form', true);

    try {
      const response = await authService.resetPassword(token!, password);

      if (response.success) {
        // Track successful password reset
        trackAuth.passwordResetSuccess();
        setIsSuccess(true);
        // Redirect to sign-in after 3 seconds
        setTimeout(() => {
          navigate('/signin');
        }, 3000);
      } else {
        // Track password reset failure
        trackAuth.passwordResetFailure(response.error || 'Unknown error');
        setError(response.error || 'Failed to reset password. Please try again.');
      }
    } catch (err: any) {
      // Track password reset failure
      trackAuth.passwordResetFailure(err.message || 'Unknown error');
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state while validating token
  if (isValidating) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-purple-500 mx-auto mb-4" />
          <p className="text-gray-400">Validating reset link...</p>
        </div>
      </div>
    );
  }

  // Invalid or expired token state
  if (!isTokenValid) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Navigation />
        <section className="relative pt-32 pb-20 px-6 overflow-hidden min-h-screen flex items-center">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-blue-900/20"></div>
          
          <div className="max-w-md mx-auto w-full relative z-10">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-red-600 to-orange-600 rounded-3xl blur-3xl opacity-20"></div>
              <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-8 md:p-10 border border-white/20">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-red-600 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <XCircle className="w-8 h-8" />
                  </div>
                  <h2 className="text-3xl font-bold mb-2">Invalid Reset Link</h2>
                  <p className="text-gray-400 mb-8">
                    This password reset link is invalid or has expired. Please request a new password reset.
                  </p>
                  
                  <Link
                    to="/forgot-password"
                    className="inline-flex items-center justify-center w-full bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-4 rounded-full font-semibold hover:shadow-2xl hover:shadow-purple-500/50 transition-all"
                  >
                    Request New Reset Link
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                  
                  <Link
                    to="/signin"
                    className="inline-flex items-center justify-center text-sm text-gray-400 hover:text-white transition-colors mt-6"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Sign In
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <Navigation />

      <section className="relative pt-32 pb-20 px-6 overflow-hidden min-h-screen flex items-center">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-blue-900/20"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)',
        }}></div>
        
        <div className="max-w-md mx-auto w-full relative z-10">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 rounded-3xl blur-3xl opacity-20"></div>
            
            <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-8 md:p-10 border border-white/20">
              
              {!isSuccess ? (
                <>
                  {/* Header */}
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <Lock className="w-8 h-8" />
                    </div>
                    <h2 className="text-3xl font-bold mb-2">Reset Password</h2>
                    <p className="text-gray-400">
                      Create a new password for your account
                      {tokenEmail && <span className="block mt-1 text-purple-400">{tokenEmail}</span>}
                    </p>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                      <p className="text-red-400 text-sm">{error}</p>
                    </div>
                  )}

                  {/* Form */}
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* New Password Field */}
                    <div>
                      <label htmlFor="password" className="block text-sm font-medium mb-2">
                        New Password
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <Lock className="w-5 h-5 text-gray-400" />
                        </div>
                        <input
                          type={showPassword ? 'text' : 'password'}
                          id="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          onFocus={() => handleFieldFocus('password')}
                          className="w-full bg-white/5 border border-white/10 rounded-xl pl-12 pr-12 py-3.5 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                          placeholder="Create a strong password"
                          required
                          disabled={isLoading}
                          minLength={8}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-white transition-colors"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Must be at least 8 characters</p>
                    </div>

                    {/* Confirm Password Field */}
                    <div>
                      <label htmlFor="confirmPassword" className="block text-sm font-medium mb-2">
                        Confirm New Password
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <Lock className="w-5 h-5 text-gray-400" />
                        </div>
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          id="confirmPassword"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          onFocus={() => handleFieldFocus('confirmPassword')}
                          className="w-full bg-white/5 border border-white/10 rounded-xl pl-12 pr-12 py-3.5 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                          placeholder="Confirm your password"
                          required
                          disabled={isLoading}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-white transition-colors"
                        >
                          {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-4 rounded-full font-semibold hover:shadow-2xl hover:shadow-purple-500/50 transition-all text-lg group flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? (
                        <>
                          <Loader className="w-5 h-5 mr-2 animate-spin" />
                          Resetting Password...
                        </>
                      ) : (
                        <>
                          Reset Password
                          <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </button>
                  </form>

                  {/* Back to Sign In */}
                  <div className="mt-8 text-center">
                    <Link 
                      to="/signin" 
                      className="inline-flex items-center text-sm text-gray-400 hover:text-white transition-colors"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back to Sign In
                    </Link>
                  </div>
                </>
              ) : (
                <>
                  {/* Success State */}
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-8 h-8" />
                    </div>
                    <h2 className="text-3xl font-bold mb-2">Password Reset!</h2>
                    <p className="text-gray-400 mb-8">
                      Your password has been successfully reset. Redirecting you to sign in...
                    </p>
                    
                    <div className="flex justify-center mb-8">
                      <Loader className="w-8 h-8 animate-spin text-purple-500" />
                    </div>

                    <Link
                      to="/signin"
                      className="inline-flex items-center justify-center w-full bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-4 rounded-full font-semibold hover:shadow-2xl hover:shadow-purple-500/50 transition-all"
                    >
                      Sign In Now
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer variant="simple" />
    </div>
  );
}
